import orderBy from 'lodash/orderBy';
import React, { useState } from 'react';
import {
  StyleSheet,
  Text,
  View,
  Platform,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useParams } from 'react-router-dom';

import { useMultiPayContractQuery } from 'apollo/queries/autogenerate/hooks';
import { MultipayContract } from 'apollo/queries/autogenerate/schemas';
import Icon from 'shared/modules/common/components/atoms/Icon';
import Loading from 'shared/modules/common/components/atoms/Loading';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';

type Pair = { key: string; value: string };

export default function MultiPayContractDetailScreen() {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: multiPayContractQuery,
    loading: isLoading,
  } = useMultiPayContractQuery({
    variables: {
      id: id as string,
    },
    fetchPolicy: 'cache-and-network',
  });
  const [multiPayTab, setMultiPayTab] = useState(0);
  const contract = multiPayContractQuery?.multipayContract || null;

  if (isLoading && !contract) {
    return <Loading id="loading-indicator" />;
  }

  if (!contract) {
    return null;
  }

  const charges = orderBy(contract.charges, 'pushAt', 'asc');
  const details: Array<Pair> = [
    {
      key: '契約日',
      value: contract.approvalDecisionAt
        ? formatYearMonthDate(contract.approvalDecisionAt, 'en')
        : '-',
    },
    { key: '分割回数', value: `${contract.charges.length}回払い` },
    {
      key: '支払日',
      value: '10日',
    },
    { key: '実質年率', value: `${contract.contractFeeRate * 100}%` },
    { key: '契約金額', value: formatToCurrency(contract.principal) },
    { key: '毎月の支払金額', value: formatToCurrency(charges[0].principal) },
    {
      key: '最終回の支払金額',
      value: formatToCurrency(charges[charges.length - 1].principal),
    },
    { key: '分割支払手数料', value: formatToCurrency(contract.contractFee) },
    {
      key: '支払総額',
      value: formatToCurrency(contract.principal + contract.contractFee),
    },
  ];

  const schedules: Array<Pair> = charges.map((item) => {
    return {
      key: formatYearMonthDate(item.pushAt, 'en'),
      value: formatToCurrency(item.principal + item.fee),
    };
  });

  const data = multiPayTab === 0 ? details : schedules;

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <NavigationBar
          title="分割払い契約の詳細"
          onBackButtonPress={() => navigate(-1)}
          id="navigation-bar"
        />
        <FlatList
          data={data}
          ListHeaderComponent={() => (
            <Header
              contract={contract}
              activeTab={multiPayTab}
              onPress={(selectedIndex: number) => setMultiPayTab(selectedIndex)}
            />
          )}
          renderItem={(item) => {
            return (
              <View style={styles.shadow}>
                <Item
                  order={item.index + 1}
                  pair={item.item}
                  isSchedule={multiPayTab === 1}
                  isTop={item.index === 0}
                  isBottom={item.index === data.length - 1}
                />
              </View>
            );
          }}
          ItemSeparatorComponent={() => (
            <View style={styles.separatorContainer}>
              <View style={styles.separator} />
            </View>
          )}
        />
      </View>
    </View>
  );
}

const Tab = ({
  title,
  tabIndex,
  activeTab,
  onPress,
}: {
  title: string;
  tabIndex: number;
  activeTab: number;
  onPress: () => void;
}) => {
  return (
    <View
      style={[
        styles.button,
        {
          backgroundColor: tabIndex === activeTab ? colors.black : colors.white,
        },
      ]}
    >
      <TouchableOpacity
        onPress={onPress}
        /** Generate HTML and Test IDs  */
        {...generateID(`multipay-contract-detail-tabs-item-${tabIndex}`)}
        /** Platform-specific props */
        {...Platform.select({
          web: {
            accessibilityRole: 'button',
          },
        })}
      >
        <Text
          style={[
            styles.buttonText,
            {
              color: tabIndex === activeTab ? colors.white : colors.black,
            },
          ]}
        >
          {title}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const Header = ({
  contract,
  activeTab,
  onPress,
}: {
  contract: MultipayContract;
  activeTab: number;
  onPress: (selectedIndex: number) => void;
}) => {
  return (
    <View>
      <View style={styles.headerTextContainer}>
        <Icon size="24" name="multi-pay-contract" />
        <Text style={[styles.headerText, { marginStart: 8 }]}>
          {contract.approvalDecisionAt
            ? `契約日 ${formatYearMonthDate(contract.approvalDecisionAt, 'en')}`
            : '審査中'}
        </Text>
        <Text style={[styles.headerText, styles.headerAmount]}>
          {formatToCurrency(contract.principal + contract.contractFee)}
        </Text>
      </View>
      <View style={styles.buttonContainer}>
        <Tab
          title="契約内容"
          tabIndex={0}
          activeTab={activeTab}
          onPress={() => onPress(0)}
        />
        <Tab
          title="支払いスケジュール"
          tabIndex={1}
          activeTab={activeTab}
          onPress={() => onPress(1)}
        />
      </View>
    </View>
  );
};

const Item = ({
  order,
  pair,
  isSchedule,
  isTop,
  isBottom,
}: {
  order: number;
  pair: Pair;
  isSchedule: boolean;
  isTop: boolean;
  isBottom: boolean;
}) => {
  const topRadius = isTop ? 8 : 0;
  const bottomRadius = isBottom ? 8 : 0;

  return (
    <View
      style={[
        styles.itemContainer,
        {
          borderTopStartRadius: topRadius,
          borderTopEndRadius: topRadius,
          borderBottomStartRadius: bottomRadius,
          borderBottomEndRadius: bottomRadius,
        },
      ]}
    >
      {isSchedule && <Text>{order}回目</Text>}
      <Text style={styles.itemKey}>{pair.key}</Text>
      <Text style={styles.itemValue}>{pair.value}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    padding: 16,
  },
  screenBigScreen: {
    padding: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  headerTextContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    ...typography.title03,
    fontSize: 16,
    fontWeight: '700',
    color: colors.greyDark,
  },
  headerAmount: {
    flex: 1,
    textAlign: 'right',
  },
  buttonContainer: {
    flex: 2,
    flexDirection: 'row',
    height: 36,
    marginVertical: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.black,
    backgroundColor: colors.white,
    overflow: 'hidden',
  },
  button: {
    flex: 1,
    justifyContent: 'center',
  },
  buttonText: {
    ...typography.caption01,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    padding: 16,
  },
  itemKey: {
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    color: colors.greyDark,
  },
  itemValue: {
    ...typography.title02,
    flex: 1,
    fontSize: 13,
    fontWeight: '400',
    color: colors.greyDark,
    textAlign: 'right',
  },
  separatorContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  separator: {
    flex: 1,
    height: 1,
    marginHorizontal: 16,
    backgroundColor: colors.greySuperLight,
  },
  shadow: {
    ...Platform.select({
      android: {
        elevation: 2,
      },
      ios: {
        shadowColor: 'rgba(0, 0, 0, 0.08)',
        shadowOpacity: 1,
        shadowRadius: 2,
        shadowOffset: {
          width: 0,
          height: 2,
        },
      },
    }),
  },
});
